import clsx from 'clsx';
import React, { memo, PropsWithChildren, useLayoutEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import urllite from 'urllite/lib/core';
import { isAnchorSectionLink, isEmailLink } from '@/utils/helpers';

const CaptureClicks = memo(({ onClick, className, children }: PropsWithChildren<any>) => {
  const history = useHistory();
  const clickRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const target = clickRef?.current;

    const handleClick = (event: MouseEvent) => {
      if (onClick) {
        const shouldProceed = onClick(event);
        if (shouldProceed === false) return;
      }

      // Ignore canceled events, modified clicks, and right clicks.
      if (event.defaultPrevented) return;

      if (event.metaKey || event.ctrlKey || event.shiftKey) return;

      if (event.button !== 0) return;

      // Get the <a> element.
      const el = event.target && ((event.target as HTMLElement).closest('a') as HTMLAnchorElement);

      // Ignore clicks from non-a elements.
      if (!el) return;

      // Ignore the click if the element has a target.
      if (el.target && el.target !== '_self') return;

      // Ignore the click if it's a download link.
      if (el.download) return;

      // Ignore hash (used often instead of javascript:void(0) in strict CSP envs)
      if (el.href === '#') return;

      // Ignore the email links (mail:test@test.com)
      if (isEmailLink(el.href)) return;

      // Use a regular expression to parse URLs instead of relying on the browser
      // to do it for us (because IE).
      const url = urllite(el.href);
      const windowURL = urllite(window.location.href);

      // Ignore links that don't share a protocol and host with ours.
      if (
        url.protocol !== '' &&
        (url.protocol !== windowURL.protocol || url.host !== windowURL.host)
      )
        return;

      // Ignore 'rel="external"' links.
      if (el.rel && /(?:^|\s+)external(?:\s+|$)/.test(el.rel)) return;

      // Prevent :focus from sticking; preventDefault() stops blur in some browsers
      el.blur();
      event.preventDefault();

      // Navigate
      history.push(url.pathname + url.search + (url.hash.length > 1 ? url.hash : ''));
    };

    if (target) {
      target.addEventListener('click', handleClick);
    }

    return () => {
      if (target) {
        target.removeEventListener('click', handleClick);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={className} ref={clickRef}>
      {children}
    </div>
  );
});

export default (Component: any) =>
  ({ onClick, containerProps, ...props }: any) => {
    return (
      <CaptureClicks
        onClick={onClick}
        className={clsx('capture-clicks', containerProps?.className)}
      >
        <Component {...props} />
      </CaptureClicks>
    );
  };
